'use strict';

/**
 * customEmbroideryModal.js – PMG-2839/PMG-2891
 *
 * Full-screen SPA-style embroidery overlay (NOT a Bootstrap modal).
 * Sits below the site header using CSS var --embroidery-header-offset.
 *
 * Open sources:
 *   - PDP  → no animation
 *   - Quick Shop (#quickViewModal) → slides out right, overlay fades in
 *
 * Preview image: Cloudinary-cropped URL built server-side from embStyleLogoCoordinates.
 * Text overlay: positioned via data-overlay-x / data-overlay-y (percentage of image).
 */

// ── State ─────────────────────────────────────────────────────────────────────
function getState($overlay) {
    var s = $overlay.data('embroidery-state');
    if (!s) {
        s = {
            text: '',
            threadColorId: '',
            threadColorName: '',
            threadColorHex: '#000000',
            designId: $overlay.data('default-tape') || 'GFS02-MONOGRAM',
            placement: $overlay.data('default-placement') || 'STANDARD LOCATION',
            hasInvalidChars: false,
            showLimitError: false
        };
        $overlay.data('embroidery-state', s);
    }
    return s;
}

// ── Text Sanitization ─────────────────────────────────────────────────────────

function sanitizeText(raw, maxChars, restrictedCombinations, allowedCharacters) {
    var inputVal = raw;
    var isOverLimit = false;

    // 1. Enforce max length
    if (inputVal.length > maxChars) {
        isOverLimit = true;
        inputVal = inputVal.slice(0, maxChars);
    }

    var upper = inputVal.toUpperCase();
    var hasInvalidChars = false;

    // 2. Enforce allowed characters (only if allowedCharacters is configured)
    if (allowedCharacters && allowedCharacters.length > 0) {
        for (var i = 0; i < upper.length; i++) {
            var char = upper.charAt(i);
            if (allowedCharacters.indexOf(char) === -1) {
                hasInvalidChars = true;
                break;
            }
        }
    } else {
        // Fallback validation if no site pref: Strip non-ASCII
        var stripped = upper.replace(/[^\u0000-\u007F]/g, '');
        var cleanedFallback = stripped.replace(/[^A-Z.\- ]/g, '');
        if (upper !== cleanedFallback) {
            hasInvalidChars = true;
        }
    }

    // 3. Check restricted combinations (profanity)
    var hasProfanity = false;
    if (upper && restrictedCombinations && restrictedCombinations.length) {
        for (var j = 0; j < restrictedCombinations.length; j++) {
            var combo = restrictedCombinations[j];
            if (typeof combo === 'string' && upper.indexOf(combo.toUpperCase()) !== -1) {
                hasProfanity = true;
                break;
            }
        }
    }

    return {
        inputValue: inputVal,
        stateValue: upper,
        isOverLimit: isOverLimit,
        hasInvalidChars: hasInvalidChars || hasProfanity
    };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isReady($overlay) {
    var state = getState($overlay);
    var hasCoords = $overlay.data('hasCoords') === true || $overlay.data('hasCoords') === 'true';
    var hasImage = $overlay.find('#embroideryPreviewImage').length > 0;

    // If it lacks coords or an image, it is permanently un-ready.
    if (!hasCoords || !hasImage) {
        return false;
    }

    return state.text.trim().length > 0 && state.threadColorId !== '' && !state.hasInvalidChars && !state.isOverLimit && !state.showLimitError;
}

function getTotalPrice($overlay) {
    var productPrice = parseFloat($overlay.data('productPrice')) || 0;
    var embroideryPrice = parseFloat($overlay.data('embroideryPrice')) || 0;
    return Math.round(productPrice + embroideryPrice);
}

function setErrors($overlay, errors) {
    var $container = $overlay.find('#embroideryValidationErrors');
    var $input = $overlay.find('#embroideryTextInput');
    var $hintText = $overlay.find('#embroideryCharHintText');


    $container.empty();

    if (errors && errors.length > 0) {
        $input.addClass('is-invalid');
        $hintText.hide();

        for (var i = 0; i < errors.length; i++) {
            var $errorItem = $('<div class="embroidery-overlay__error-item"><i class="fa fa-exclamation-circle error-message-icon" aria-hidden="true"></i><span>' + errors[i] + '</span></div>');
            $container.append($errorItem);
        }
    } else {
        $input.removeClass('is-invalid');
        $hintText.show(); // Show the "6 characters max" text
    }
}

function updateCTAState($overlay) {
    var $btn = $overlay.find('#embroideryAddToCartBtn');
    var $text = $overlay.find('#embroideryAtcText');
    var $price = $overlay.find('#embroideryAtcPrice');

    var hasCoords = $overlay.data('hasCoords') === true || $overlay.data('hasCoords') === 'true';
    var hasImage = $overlay.find('#embroideryPreviewImage').length > 0;

    if (!hasCoords || !hasImage) {
        $btn.prop('disabled', true);
        $text.text('PREVIEW UNAVAILABLE');
        $price.text('');

        // Also show an error message instead of hint
        setErrors($overlay, ['Embroidery configuration is incomplete for this item.']);
        return;
    }

    var total = ' | <span class="embroidery-overlay__price-currency">$</span>' + getTotalPrice($overlay);
    $price.html(total);

    if (isReady($overlay)) {
        $btn.prop('disabled', false).removeClass('embroidery-overlay__atc-btn--incomplete');
        $text.text('ADD TO CART');
    } else {
        $btn.prop('disabled', false).addClass('embroidery-overlay__atc-btn--incomplete');
        $text.text('CHOOSE TEXT & COLOR');
    }
}


// ── Text Overlay ──────────────────────────────────────────────────────────────

function updateTextOverlay($overlay) {
    var state = getState($overlay);

    // Update both desktop and mobile text spans
    var $spans = $overlay.find('#embroideryOverlayText, #embroideryOverlayTextMobile');
    $spans.text(state.text);
    $spans.css('color', state.threadColorHex);

    var $overlayEls = $overlay.find('#embroideryTextOverlay, #embroideryTextOverlayMobile');

    if (!state.text || !state.threadColorId || state.hasInvalidChars) {
        $overlayEls.hide();
        return;
    }

    $overlayEls.show();

    // Position using data attributes set from server-side coords
    var rawX = $overlay.data('overlayX');
    var rawY = $overlay.data('overlayY');

    var numX = rawX !== undefined ? parseFloat(rawX) : 50;
    var numY = rawY !== undefined ? parseFloat(rawY) : 50;

    var isPctX = (String(rawX).indexOf('%') !== -1) || (numX <= 100);
    var isPctY = (String(rawY).indexOf('%') !== -1) || (numY <= 100);

    var leftVal = isPctX ? numX + '%' : numX + 'px';
    var topVal = isPctY ? numY + '%' : numY + 'px';

    $overlayEls.css({
        top: topVal,
        left: leftVal
    });
}

// ── Header offset ─────────────────────────────────────────────────────────────

function setHeaderOffset($overlay) {
    var $header = $('header');
    var headerHeight = $header.length ? $header.outerHeight() : 0;
    $overlay.css('--embroidery-header-offset', headerHeight + 'px');
}

function syncVisualViewport() {
    var vv = window.visualViewport;
    if (!vv) return;
    document.documentElement.style.setProperty('--embroidery-vv-height', vv.height + 'px');

    var input = document.activeElement;
    if (input && input.id === 'embroideryTextInput') {
        window.requestAnimationFrame(function () {
            var overlay = input.closest('.js-embroidery-overlay');
            var overflow = (input.getBoundingClientRect().bottom + 16) - overlay.getBoundingClientRect().bottom;
            if (overflow > 0) overlay.scrollTop += overflow;
        });
    }
}

function resetWindowScroll() {
    if (window.scrollY) window.scrollTo(0, 0);
}

var lastTouchY = 0;

function onTouchStart(e) {
    lastTouchY = e.touches[0].clientY;
}

function onTouchMove(e) {
    var y = e.touches[0].clientY;
    var dy = y - lastTouchY;
    var overlay = e.target.closest('.js-embroidery-overlay');
    lastTouchY = y;

    if (overlay) {
        var atTop = overlay.scrollTop <= 0;
        var atBottom = overlay.scrollTop + overlay.clientHeight >= overlay.scrollHeight - 1;
        if (!(dy > 0 && atTop) && !(dy < 0 && atBottom)) return;
    }
    e.preventDefault();
}

function bindVisualViewport() {
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scroll', resetWindowScroll);
    if (!window.visualViewport) return;
    window.visualViewport.addEventListener('resize', syncVisualViewport);
    syncVisualViewport();
}

function unbindVisualViewport() {
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('scroll', resetWindowScroll);
    if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', syncVisualViewport);
    }
    document.documentElement.style.removeProperty('--embroidery-vv-height');
}

var eddCache = {};

function fetchEdd($overlay) {
    var eddUrl = $overlay.data('eddUrl');
    var $eddContainer = $overlay.find('#embroideryEddContainer');

    if (!eddUrl || $eddContainer.length === 0) {
        return;
    }

    if (eddCache[eddUrl] && typeof eddCache[eddUrl] === 'object') {
        var cached = eddCache[eddUrl];
        $eddContainer.empty();
        var html = '<strong>' + cached.eddMessage + '</strong>';
        if (cached.eddHint) {
            html += ' <span>' + cached.eddHint + '</span>';
        }
        $eddContainer.html(html);
        return;
    }

    if (eddCache[eddUrl] === true) {
        return; // Pending, do not double-call
    }

    eddCache[eddUrl] = true; // Mark as pending

    $.ajax({
        url: eddUrl,
        type: 'GET',
        success: function (response) {
            if (response && response.success && response.eddMessage) {
                var entry = {
                    eddMessage: response.eddMessage,
                    eddHint: response.eddHint || ''
                };
                eddCache[eddUrl] = entry;
                $eddContainer.empty();
                var responseHtml = '<strong>' + entry.eddMessage + '</strong>';
                if (entry.eddHint) {
                    responseHtml += ' <span>' + entry.eddHint + '</span>';
                }
                $eddContainer.html(responseHtml);
            } else {
                delete eddCache[eddUrl]; // Allow retry if failed
            }
        },
        error: function () {
            delete eddCache[eddUrl]; // Allow retry if failed
        }
    });
}


function showOverlay($overlay, addClass) {
    setHeaderOffset($overlay);
    $overlay.addClass('is-open');
    if (addClass) {
        $overlay.addClass(addClass);
    }
    $('body').addClass('embroidery-overlay-open');
    $('body').toggleClass('hide-zendesk', true);
    bindVisualViewport();

    // Clear any previous validation errors
    $overlay.find('#embroideryValidationErrors').empty();
    $overlay.find('#embroideryTextInput').removeClass('is-invalid');
    $overlay.find('#embroideryCharHintText').show();

    fetchEdd($overlay);

    // ADA: Move focus to the first focusable element (close button)
    setTimeout(function () {
        var $closeBtn = $overlay.find('.embroidery-overlay__close');
        if ($closeBtn.length) {
            $closeBtn.focus();
        } else {
            $overlay.find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])').filter(':visible').first().focus();
        }
    }, 100);
}

function openOverlay($overlay, source) {
    $overlay.data('source', source || 'pdp');

    if (source === 'quickshop') {
        var pdpUrl = $('#quickViewModal .pdp-link').attr('href');
        if (pdpUrl) {
            var newUrl = pdpUrl + (pdpUrl.indexOf('?') > -1 ? '&' : '?') + 'isCustomEmbroidery=true';
            if (!$overlay.data('original-url')) {
                $overlay.data('original-url', window.location.href);
            }
            window.history.replaceState({ embroideryOpen: true }, document.title, newUrl);
        }

        // Slide #quickViewModal out to the right, then show overlay
        var $quickModal = $('#quickViewModal');
        if ($quickModal.hasClass('show')) {
            $quickModal.addClass('embroidery-slide-out');
            setTimeout(function () {
                $quickModal.modal('hide');
                $quickModal.removeClass('embroidery-slide-out');
                showOverlay($overlay, 'from-quickshop');
            }, 250);
        } else {
            showOverlay($overlay, 'from-quickshop');
        }
    } else {
        showOverlay($overlay, 'from-pdp');
    }

    // ADA: Focus trap
    $overlay.off('keydown.embroideryFocusTrap').on('keydown.embroideryFocusTrap', function (e) {
        if (e.key === 'Tab' || e.keyCode === 9) {
            var focusableElements = $overlay.find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])').filter(':visible');
            if (focusableElements.length === 0) return;

            var first = focusableElements.first()[0];
            var last = focusableElements.last()[0];

            if (e.shiftKey) { // Shift + Tab
                if (document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                }
            } else if (document.activeElement === last) { // Tab
                first.focus();
                e.preventDefault();
            }
        }
    });
}

function resetState($overlay) {
    var state = getState($overlay);
    state.text = '';
    state.threadColorId = '';
    state.threadColorName = '';
    state.threadColorHex = '#000000';
    state.hasInvalidChars = false;
    state.showLimitError = false;

    var $defaultSwatch = $overlay.find('.embroidery-overlay__color-swatch').filter(function () {
        return $(this).attr('data-default') === 'true' || $(this).data('default') === true;
    });

    $overlay.find('.embroidery-overlay__color-swatch').removeClass('is-selected').attr({ 'aria-checked': 'false', tabindex: '-1' });

    if ($defaultSwatch.length) {
        state.threadColorId = $defaultSwatch.data('colorId') || '';
        state.threadColorName = $defaultSwatch.data('colorName') || '';
        state.threadColorHex = $defaultSwatch.data('colorValue') || '#000000';
        $defaultSwatch.addClass('is-selected').attr({ 'aria-checked': 'true', tabindex: '0' });
    } else {
        state.threadColorName = '';
        $overlay.find('.embroidery-overlay__color-swatch').first().attr('tabindex', '0');
    }

    $overlay.find('#embroiderySelectedColorName').text(state.threadColorName);
    $overlay.find('#embroideryTextInput').val('');
    setErrors($overlay, []);
    updateTextOverlay($overlay);
    updateCTAState($overlay);
}

function closeOverlay($overlay, skipQuickView) {
    var source = $overlay.data('source');

    // Redirect to PDP if we're going back from Quick Shop
    if (source === 'quickshop' && !skipQuickView) {
        var originalUrl = $overlay.data('original-url');
        if (originalUrl) {
            window.history.replaceState({}, document.title, originalUrl);
        }
        var pdpUrl = $('#quickViewModal .pdp-link').attr('href');
        if (pdpUrl) {
            $.spinner().start();
            window.location.href = pdpUrl;
            return; // Keep modal open during redirect
        }
        var $quickModal = $('#quickViewModal');
        if ($quickModal.length && !$quickModal.hasClass('show')) {
            $quickModal.modal('show');
        }
    }

    $overlay.removeClass('is-open from-quickshop from-pdp');
    $('body').removeClass('embroidery-overlay-open');
    $('body').toggleClass('hide-zendesk', false);
    unbindVisualViewport();

    $overlay.off('keydown.embroideryFocusTrap');

    // ADA: Restore focus to trigger button
    var $triggerBtn = $overlay.data('triggerButton');
    if ($triggerBtn && $triggerBtn.length) {
        $triggerBtn.focus();
        $overlay.removeData('triggerButton');
    }
    resetState($overlay);
}

// ── Add to Cart ──────────────────────────────────────────────────────────────
function addToCart($overlay) {
    var state = getState($overlay);

    var hasCoords = $overlay.data('hasCoords') === true || $overlay.data('hasCoords') === 'true';
    var hasImage = $overlay.find('#embroideryPreviewImage').length > 0;

    if (!hasCoords || !hasImage) {
        setErrors($overlay, ['Embroidery configuration is incomplete for this item.']);
        return;
    }

    if (!isReady($overlay)) {
        if (state.hasInvalidChars || state.isOverLimit || state.showLimitError) {
            $overlay.find('#embroideryTextInput').trigger('keyup.embroideryInput');
        } else if (state.text.trim().length === 0 || state.threadColorId === '') {
            setErrors($overlay, ['Please enter your custom text and select a thread color.']);
        } else {
            $overlay.find('#embroideryTextInput').trigger('keyup.embroideryInput');
        }
        return;
    }

    var $btn = $overlay.find('#embroideryAddToCartBtn');

    // Find ATC URL — try overlay data attr, then form, then page
    var addToCartUrl = $overlay.data('addToCartUrl');
    if (!addToCartUrl) {
        addToCartUrl = $('.add-to-cart-url').first().val()
            || $('button.add-to-cart').first().data('atcUrl');
    }

    if (!addToCartUrl) {
        setErrors($overlay, ['Add to cart URL not found. Please refresh the page.']);
        return;
    }

    var pid = $overlay.data('pid');
    var qty = parseInt($('.quantity-select, #quantity').first().val(), 10) || 1;

    $btn.prop('disabled', true);
    $.spinner().start();

    $.ajax({
        url: addToCartUrl,
        method: 'POST',
        data: {
            pid: pid,
            quantity: qty,
            embroideryEnabled: true,
            embroideryText: state.text,
            embroideryThreadColorId: state.threadColorId,
            embroideryThreadColorLabel: state.threadColorName,
            embroideryDesignId: state.designId,
            embroideryPlacement: state.placement,
            embroideryPrice: $overlay.data('embroideryPrice')
        },
        success: function (data) {
            $.spinner().stop();
            if (data.error) {
                setErrors($overlay, [data.errorMessage || 'An error occurred. Please try again.']);
                updateCTAState($overlay);
                return;
            }

            // Close overlay but don't reopen quick view
            closeOverlay($overlay, true);

            // Update mini-cart
            $('body').trigger('product:afterAddToCart', data);
            $('.minicart').data('pid', pid);
            $('.minicart').trigger('count:update', data);
            $('body').trigger('updateCartPage', data);

            if (data.quantityTotal !== undefined) {
                $('.minicart-quantity').text(data.quantityTotal);
            }

            // Open mini-cart drawer
            var $minicartLink = $('.minicart-link, .js-minicart-link, .minicart .icon').first();
            if ($minicartLink.length) {
                $minicartLink.trigger('click');
            }
        },
        error: function () {
            $.spinner().stop();
            setErrors($overlay, ['An error occurred. Please try again.']);
            updateCTAState($overlay);
        }
    });
}

// ── Init ──────────────────────────────────────────────────────────────────────

function initOverlay() {
    $('.js-embroidery-overlay').each(function () {
        var $overlay = $(this);
        if ($overlay.parent()[0] !== document.body) {
            // Cleanup old quickshop overlays
            if ($overlay.closest('#quickViewModal, .quick-modal').length) {
                $('body > .js-embroidery-overlay[data-source="quickshop"]').remove();
                $overlay.attr('data-source', 'quickshop');
            } else {
                $overlay.attr('data-source', 'pdp');
            }

            // Link overlay to container
            var uniqueId = 'embroidery-' + Math.random().toString(36).substr(2, 9);
            $overlay.attr('id', uniqueId);
            var $container = $overlay.closest('.product-detail, .quick-modal, .set-item');
            $container.attr('data-embroidery-id', uniqueId);
            $container.find('.custom-embroidery-cta-start-here').attr('data-embroidery-target', uniqueId);

            $overlay.appendTo('body');

            var state = getState($overlay);
            var $selectedSwatch = $overlay.find('.embroidery-overlay__color-swatch.is-selected');

            if ($selectedSwatch.length) {
                state.threadColorId = $selectedSwatch.data('colorId') || '';
                state.threadColorName = $selectedSwatch.data('colorName') || '';
                state.threadColorHex = $selectedSwatch.data('colorValue') || '#000000';
                $overlay.find('#embroiderySelectedColorName').text(state.threadColorName);
            } else {
                $overlay.find('#embroiderySelectedColorName').text('');
            }
            updateCTAState($overlay);
            $overlay.find('#embroideryTextOverlay').hide();

            // Build static inline tooltip (no Bootstrap tooltip plugin — avoids all mobile event conflicts)
            var $tooltipTrigger = $overlay.find('[data-toggle="embroidery-tooltip"]');
            if ($tooltipTrigger.length) {
                var helperHtml = $tooltipTrigger.data('helper-template') || $tooltipTrigger.attr('data-original-title') || '';
                var tooltipId = $tooltipTrigger.attr('id') + '-content';
                // Remove existing if reinit
                $tooltipTrigger.closest('.embroidery-overlay__char-hint').find('.embroidery-overlay__tooltip-content').remove();
                if (helperHtml) {
                    var $staticTooltip = $('<div class="tooltip bs-tooltip-top embroidery-overlay__tooltip-content" role="tooltip" id="' + tooltipId + '"><div class="tooltip-arrow"></div><div class="tooltip-inner">' + helperHtml + '</div></div>');
                    $tooltipTrigger.closest('.embroidery-overlay__char-hint').append($staticTooltip);
                }
                // Switch toggle attribute so global toolTip.js ignores it completely
                $tooltipTrigger.attr('data-embroidery-tooltip', 'true').removeAttr('data-toggle');
                $tooltipTrigger.data('tooltip-visible', false);
            }
        }
    });

    // Delegate events using body so we don't bind multiple times
    if (!$('body').data('embroidery-bound')) {
        $('body').data('embroidery-bound', true);

        // Recalculate header offset on window resize
        $(window).on('resize.embroidery', function () {
            var $openOverlay = $('.js-embroidery-overlay.is-open');
            if ($openOverlay.length) {
                setHeaderOffset($openOverlay);
            }
        });

        // ── Helper functions for custom tooltip ──────────────────────────────
        var showEmbroideryTooltip = function ($btn) {
            var $overlay = $btn.closest('.js-embroidery-overlay');
            var $content = $overlay.find('.embroidery-overlay__tooltip-content');
            if (!$content.length) return;
            $content.addClass('is-visible');
            $btn.data('tooltip-visible', true);
            $btn.attr('aria-expanded', 'true');

            var textContent = $content.find('.tooltip-inner').text() || '';
            var liveRegionId = $btn.attr('id') + '-live-modal';
            var $liveRegion = $overlay.find('#' + liveRegionId);
            if (!$liveRegion.length) {
                $liveRegion = $('<div id="' + liveRegionId + '" class="sr-only" aria-live="assertive" aria-atomic="true"></div>');
                $overlay.append($liveRegion);
            }
            $liveRegion.text('');
            setTimeout(function () { $liveRegion.text(textContent); }, 50);
        };

        var hideEmbroideryTooltip = function ($btn) {
            var $overlay = $btn.closest('.js-embroidery-overlay');
            $overlay.find('.embroidery-overlay__tooltip-content').removeClass('is-visible');
            $btn.data('tooltip-visible', false);
            $btn.data('opened-by', null);
            $btn.attr('aria-expanded', 'false');
        };

        // Desktop hover
        $('body')
            .on('mouseenter.embroideryTooltip', '[data-embroidery-tooltip]', function () {
                var $btn = $(this);
                $btn.data('btn-hovered', true);
                clearTimeout($btn.data('hide-timeout'));

                if ($btn.data('touch-started')) return; // Ignore simulated hover on touch

                if (!$btn.data('tooltip-visible')) {
                    $btn.data('opened-by', 'hover');
                    showEmbroideryTooltip($btn);
                }
            })
            .on('mouseleave.embroideryTooltip', '[data-embroidery-tooltip]', function () {
                var $btn = $(this);
                $btn.data('btn-hovered', false);

                if ($btn.data('opened-by') === 'click') return;
                var $overlay = $btn.closest('.js-embroidery-overlay');
                var $content = $overlay.find('.embroidery-overlay__tooltip-content');

                var timeoutId = setTimeout(function () {
                    var contentHovered = $content.data('content-hovered') || $content.is(':hover');
                    var btnHovered = $btn.data('btn-hovered') || $btn.is(':hover');
                    if (!contentHovered && !btnHovered) {
                        hideEmbroideryTooltip($btn);
                    }
                }, 300);
                $btn.data('hide-timeout', timeoutId);
            })
            .on('mouseenter.embroideryTooltipContent', '.embroidery-overlay__tooltip-content', function () {
                var $content = $(this);
                $content.data('content-hovered', true);
                var $overlay = $content.closest('.js-embroidery-overlay');
                var $btn = $overlay.find('[data-embroidery-tooltip]');
                if ($btn.length) {
                    clearTimeout($btn.data('hide-timeout'));
                }
            })
            .on('mouseleave.embroideryTooltipContent', '.embroidery-overlay__tooltip-content', function () {
                var $content = $(this);
                $content.data('content-hovered', false);
                var $overlay = $content.closest('.js-embroidery-overlay');
                var $btn = $overlay.find('[data-embroidery-tooltip]');
                if (!$btn.length || $btn.data('opened-by') === 'click') return;

                var timeoutId = setTimeout(function () {
                    var contentHovered = $content.data('content-hovered') || $content.is(':hover');
                    var btnHovered = $btn.data('btn-hovered') || $btn.is(':hover');
                    if (!btnHovered && !contentHovered) {
                        hideEmbroideryTooltip($btn);
                    }
                }, 300);
                $btn.data('hide-timeout', timeoutId);
            })
            .on('touchstart.embroideryTooltip', '[data-embroidery-tooltip]', function () {
                // Do not preventDefault here because it's a passive listener in Chrome.
                // Instead, just set a flag to prevent the simulated mouseenter from double-toggling.
                $(this).data('touch-started', true);
            })
            .on('click.embroideryTooltip', '[data-embroidery-tooltip]', function (e) {
                e.preventDefault();
                var $btn = $(this);

                // Reset touch flag after a short delay
                if ($btn.data('touch-started')) {
                    setTimeout(function () {
                        $btn.data('touch-started', false);
                    }, 500);
                }

                if ($btn.data('tooltip-visible')) {
                    hideEmbroideryTooltip($btn);
                } else {
                    $btn.data('opened-by', 'click');
                    showEmbroideryTooltip($btn);
                }
            });

        // Close on outside click/touch
        $(document).on('click.embroideryTooltipOutside touchstart.embroideryTooltipOutside', function (e) {
            if (!$(e.target).closest('[data-embroidery-tooltip]').length &&
                !$(e.target).closest('.embroidery-overlay__tooltip-content').length) {
                var $btn = $('[data-embroidery-tooltip]');
                if ($btn.length && $btn.data('tooltip-visible')) {
                    hideEmbroideryTooltip($btn);
                }
            }
        });

        // CTA Button
        $('body').on('click.embroideryCta', '.custom-embroidery-cta-start-here', function (e) {
            e.preventDefault();
            var $ctaBtn = $(this);
            var targetId = $ctaBtn.attr('data-embroidery-target');

            if (!targetId) {
                initOverlay();
                targetId = $ctaBtn.attr('data-embroidery-target');
            }

            var $productContainer = $ctaBtn.closest('.product-detail, .quick-modal, .set-item');
            var overlayId = targetId || $productContainer.attr('data-embroidery-id');
            var $overlay = $('#' + overlayId);
            if ($overlay.length) {
                var source = 'pdp';
                if ($productContainer.hasClass('quick-modal') || $ctaBtn.closest('.quick-modal').length > 0) {
                    source = 'quickshop';
                }
                $overlay.data('triggerButton', $ctaBtn);
                openOverlay($overlay, source);
            }
        });

        // Close Buttons
        $('body').on('click.embroideryClose', '.js-embroidery-overlay #embroideryCloseBtn, .js-embroidery-overlay #embroideryBackBtn', function (e) {
            e.preventDefault();
            closeOverlay($(this).closest('.js-embroidery-overlay'));
        });

        // ESC key
        $(document).on('keydown.embroideryOverlay', function (e) {
            if (e.key === 'Escape') {
                $('.js-embroidery-overlay.is-open').each(function () {
                    closeOverlay($(this));
                });
            }
        });

        // Resize
        var resizeTimer;
        $(window).on('resize.embroideryOverlay', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function () {
                $('.js-embroidery-overlay.is-open').each(function () {
                    var $ov = $(this);
                    var $header = $('header');
                    var headerHeight = $header.length ? $header.outerHeight() : 0;
                    $ov.css('--embroidery-header-offset', headerHeight + 'px');
                });
            }, 100);
        });

        // Keydown
        $('body').on('keydown.embroideryInput', '.js-embroidery-overlay #embroideryTextInput', function (e) {
            var $overlay = $(this).closest('.js-embroidery-overlay');

            // UX / ADA: Accept input on Enter and move focus to next logical element to dismiss mobile keyboard safely
            if (e.key === 'Enter') {
                e.preventDefault();
                var $colorBtn = $overlay.find('.embroidery-overlay__color-swatch').first();
                if ($colorBtn.length) {
                    $colorBtn.focus();
                } else {
                    $overlay.find('#embroideryAddToCartBtn').focus();
                }
                return;
            }

            var state = getState($overlay);
            var $input = $(this);
            var maxChars = parseInt($overlay.attr('data-max-chars') || $overlay.data('maxChars'), 10) || 6;

            // If input is full and user tries to type a printable character (length === 1), show error
            if ($input.val().length >= maxChars) {
                if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    state.showLimitError = true;
                    e.preventDefault();
                } else if (e.key === 'Backspace' || e.key === 'Delete') {
                    state.showLimitError = false;
                }
            } else {
                state.showLimitError = false;
            }
        });

        // Blur
        $('body').on('blur.embroideryInput focusout.embroideryInput', '.js-embroidery-overlay #embroideryTextInput', function (e) {
            if (e.type === 'focusout') {
                e.stopPropagation(); // Prevent clientSideValidation.js from clearing custom errors
            }
            var $overlay = $(this).closest('.js-embroidery-overlay');
            var state = getState($overlay);
            state.showLimitError = false;

            var val = $(this).val();
            if (val !== val.trim()) {
                $(this).val(val.trim());
            }

            $(this).trigger('keyup.embroideryInput');
        });

        // Keyup
        $('body').on('keyup.embroideryInput', '.js-embroidery-overlay #embroideryTextInput', function () {
            var $overlay = $(this).closest('.js-embroidery-overlay');
            var state = getState($overlay);
            var $input = $(this);
            var rawVal = $input.val();
            var maxChars = parseInt($overlay.attr('data-max-chars') || $overlay.data('maxChars'), 10) || 6;

            // Site prefs
            var restrictedCombinations = [];
            try {
                restrictedCombinations = JSON.parse($overlay.attr('data-restricted') || '[]');
            } catch (ex) {
                // ignore
            }
            var allowedCharacters = [];
            try {
                allowedCharacters = JSON.parse($overlay.attr('data-allowed') || '[]');
            } catch (ex) {
                // ignore
            }

            var result = sanitizeText(rawVal, maxChars, restrictedCombinations, allowedCharacters);
            $input.val(result.inputValue);

            // Only update preview with up to maxChars
            state.text = result.stateValue.slice(0, maxChars);
            state.hasInvalidChars = result.hasInvalidChars;
            state.isOverLimit = result.isOverLimit;

            var errors = [];
            if (state.isOverLimit || state.showLimitError) {
                errors.push('Character limit exceeded');
            }
            if (state.hasInvalidChars) {
                errors.push('This text is not allowed');
            }

            setErrors($overlay, errors);
            updateTextOverlay($overlay);
            updateCTAState($overlay);
        });

        // Add to Cart Click
        $('body').on('click.embroideryAtc', '.js-embroidery-overlay #embroideryAddToCartBtn', function () {
            var $overlay = $(this).closest('.js-embroidery-overlay');
            addToCart($overlay);
        });

        // Swatches
        $('body').on('click.embroideryColorSwatch', '.js-embroidery-overlay .embroidery-overlay__color-swatch', function () {
            var $overlay = $(this).closest('.js-embroidery-overlay');
            var state = getState($overlay);
            var $swatch = $(this);

            $overlay.find('.embroidery-overlay__color-swatch').removeClass('is-selected').attr({
                'aria-checked': 'false',
                tabindex: '-1'
            });
            $swatch.addClass('is-selected').attr({
                'aria-checked': 'true',
                tabindex: '0'
            });

            state.threadColorId = $swatch.data('colorId') || '';
            state.threadColorName = $swatch.data('colorName') || '';
            state.threadColorHex = $swatch.data('colorValue') || '#000000';

            $overlay.find('#embroiderySelectedColorName').text(state.threadColorName);
            updateTextOverlay($overlay);
            updateCTAState($overlay);
        });

        // Swatches Keyboard Navigation (ADA)
        $('body').on('keydown.embroideryColorSwatch', '.js-embroidery-overlay .embroidery-overlay__color-swatch', function (e) {
            if (e.which !== 37 && e.which !== 38 && e.which !== 39 && e.which !== 40) return;
            e.preventDefault();

            var $swatches = $(this).closest('.embroidery-overlay__color-swatches').find('.embroidery-overlay__color-swatch');
            var currentIndex = $swatches.index(this);
            var nextIndex;

            if (e.which === 37 || e.which === 38) { // left or up
                nextIndex = currentIndex > 0 ? currentIndex - 1 : $swatches.length - 1;
            } else { // right or down
                nextIndex = currentIndex < $swatches.length - 1 ? currentIndex + 1 : 0;
            }

            $swatches.eq(nextIndex).trigger('click').trigger('focus');
        });

        // Image Load
        $('body').on('load.embroidery', '.js-embroidery-overlay #embroideryPreviewImage', function () {
            var $overlay = $(this).closest('.js-embroidery-overlay');
            var state = getState($overlay);
            if (state.text) {
                updateTextOverlay($overlay);
            }
        });
    }
}

// ── Variant change listener ───────────────────────────────────────────────────
function bindVariantChangeListener() {
    $(document).off('product:afterAttributeSelect.embroidery').on('product:afterAttributeSelect.embroidery', function (e, data) {
        if (!data || !data.data || !data.data.product) return;
        var product = data.data.product;
        var $productContainer = data.container;
        if (!$productContainer || !$productContainer.length) return;

        var overlayId = $productContainer.attr('data-embroidery-id');
        if (!overlayId) return;

        var $overlay = $('#' + overlayId);
        if (!$overlay.length) return;

        // Update PID
        if (product.id) {
            $overlay.data('pid', product.id);
        }

        // Update product name
        if (product.productName) {
            $overlay.find('.embroidery-overlay__product-name').text(product.productName);
        }

        // Update modal image
        var previewSrc = '';
        if (product.customEmbroidery && product.customEmbroidery.previewImageUrl) {
            previewSrc = product.customEmbroidery.previewImageUrl;
        } else if (product.images && product.images['pdp-main-desktop'] && product.images['pdp-main-desktop'].length > 0) {
            previewSrc = product.images['pdp-main-desktop'][0].url;
        } else if (product.images && product.images.large && product.images.large.length > 0) {
            previewSrc = product.images.large[0].url;
        }

        if (previewSrc) {
            $overlay.find('.embroidery-overlay__preview-image').attr('src', previewSrc).removeClass('d-none');
            $overlay.find('.embroidery-overlay__preview-error').addClass('d-none');
        } else {
            $overlay.find('.embroidery-overlay__preview-image').addClass('d-none');
            $overlay.find('.embroidery-overlay__preview-error').removeClass('d-none');
        }

        // Update product price
        if (product.price && product.price.sales) {
            $overlay.data('productPrice', product.price.sales.decimalPrice);
        }

        // Update embroidery price from site pref
        if (product.customEmbroidery && product.customEmbroidery.price) {
            $overlay.data('embroideryPrice', product.customEmbroidery.price);
        }

        // Update preview image
        if (product.customEmbroidery && product.customEmbroidery.logoCoordinatesRaw) {
            $overlay.data('embStyleLogoCoordinates', product.customEmbroidery.logoCoordinatesRaw);
            $overlay.data('hasCoords', true);
            $overlay.attr('data-has-coords', 'true');
            updateTextOverlay($overlay); // Re-calculates placement and updates image src
        } else {
            $overlay.data('hasCoords', false);
            $overlay.attr('data-has-coords', 'false');
        }

        // Update maxChars on variant change
        if (product.customEmbroidery && typeof product.customEmbroidery.maxCharacters !== 'undefined') {
            var maxChars = parseInt(product.customEmbroidery.maxCharacters, 10) || 6;
            $overlay.attr('data-max-chars', maxChars);
            $overlay.data('maxChars', maxChars);

            $overlay.find('#embroideryTextInput').attr('maxlength', maxChars + 1);

            var $hintText = $overlay.find('#embroideryCharHintText');
            if ($hintText.length && $hintText.data('template')) {
                var hintTemplate = $hintText.data('template');
                $hintText.text(hintTemplate.replace('{n}', maxChars));
            }

            // Update tooltip text
            var $tooltipContent = $overlay.find('#embroideryTooltipContent');
            if ($tooltipContent.length && $tooltipContent.data('helperTemplate')) {
                var tooltipTemplate = $tooltipContent.data('helperTemplate');
                $tooltipContent.html(tooltipTemplate.replace(/\{n\}/g, maxChars));
            }
        } else {
            // Hide embroidery options if no longer eligible
            $overlay.removeClass('is-open');
        }

        // Final CTA state update after all data attributes (like hasCoords) are set
        updateCTAState($overlay);
    });
}

function updateMaxCharsOnLoad() {
    $('.js-embroidery-overlay').each(function () {
        var $overlay = $(this);
        var maxChars = parseInt($overlay.attr('data-max-chars') || $overlay.data('maxChars'), 10) || 6;

        // Update input maxlength to maxChars + 1 so the limit error can be triggered
        $overlay.find('#embroideryTextInput').attr('maxlength', maxChars + 1);

        // Update static hint text
        var $hintText = $overlay.find('#embroideryCharHintText');
        if ($hintText.length && $hintText.data('template')) {
            var hintTemplate = $hintText.data('template');
            $hintText.text(hintTemplate.replace('{n}', maxChars));
        }

        // Update tooltip text
        var $tooltipTrigger = $overlay.find('[data-toggle="embroidery-tooltip"]');
        if ($tooltipTrigger.length && $tooltipTrigger.data('helperTemplate')) {
            var tooltipTemplate = $tooltipTrigger.data('helperTemplate');
            $tooltipTrigger.attr('data-original-title', tooltipTemplate.replace(/\{n\}/g, maxChars));
        }
    });
}

// ── Export ────────────────────────────────────────────────────────────────────

module.exports = {
    init: function () {
        initOverlay();
        bindVariantChangeListener();
        updateMaxCharsOnLoad();
    }
};
